/**
 * Preferences — global theme + mute state.
 *
 * Two settings, persisted to localStorage under `rendever-games.prefs`:
 *  - `theme`: 'light' | 'dark'   (default: from prefers-color-scheme)
 *  - `muted`: boolean            (default: false)
 *
 * Theme side-effect: mirrored to `document.documentElement.dataset.theme`
 * so the design tokens in `styles/tokens.css` apply the right palette.
 *
 * Mute side-effect: read by `juice/audio.ts` via `isMuted()`, which is also
 * a synchronous read of the same localStorage key. We don't need an
 * imperative subscription from juice → preferences — every call site in
 * variant SFX code wraps in `playIfNotMuted`, which reads localStorage
 * directly. Writes here cause subsequent SFX calls to no-op immediately.
 *
 * Multi-root sync: the trivia GameModule mounts a separate React root
 * inside the GameHost container, with its own PreferencesProvider so the
 * variant components can use the hook. The two provider instances stay in
 * sync via a window-level CustomEvent that every Provider dispatches when
 * its state changes and listens for to refresh its own state.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

const STORAGE_KEY = 'rendever-games.prefs';
const SYNC_EVENT = 'rendever-games:prefs-changed';

export type Theme = 'light' | 'dark';

export interface PreferencesValue {
  theme: Theme;
  setTheme: (t: Theme) => void;
  muted: boolean;
  setMuted: (m: boolean) => void;
  /** Convenience toggle helpers. */
  toggleTheme: () => void;
  toggleMute: () => void;
}

interface StoredPrefs {
  theme?: Theme;
  muted?: boolean;
}

const PreferencesContext = createContext<PreferencesValue | null>(null);

function readStored(): StoredPrefs {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    return JSON.parse(raw) as StoredPrefs;
  } catch {
    return {};
  }
}

function writeStored(prefs: StoredPrefs): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  } catch {
    /* Quota errors / private browsing — best-effort only. */
  }
}

function systemTheme(): Theme {
  if (typeof window === 'undefined') return 'light';
  if (window.matchMedia?.('(prefers-color-scheme: dark)').matches) return 'dark';
  return 'light';
}

export function PreferencesProvider({ children }: { children: ReactNode }) {
  const [{ theme, muted }, setState] = useState<{ theme: Theme; muted: boolean }>(
    () => {
      const stored = readStored();
      return {
        theme: stored.theme ?? systemTheme(),
        muted: stored.muted ?? false,
      };
    },
  );

  // Apply theme to <html data-theme>. Tokens CSS picks it up.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    document.documentElement.dataset['theme'] = theme;
  }, [theme]);

  // Persist + broadcast whenever the state changes.
  useEffect(() => {
    writeStored({ theme, muted });
    if (typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent(SYNC_EVENT, { detail: { theme, muted } }),
      );
    }
  }, [theme, muted]);

  // Subscribe to broadcasts from peer Provider instances (the inner trivia
  // root or any other separate React root). Storage events also catch
  // cross-tab changes.
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handler = (event: Event): void => {
      const detail = (event as CustomEvent<{ theme: Theme; muted: boolean }>)
        .detail;
      if (!detail) return;
      setState((s) =>
        s.theme === detail.theme && s.muted === detail.muted ? s : detail,
      );
    };

    const storageHandler = (event: StorageEvent): void => {
      if (event.key !== STORAGE_KEY) return;
      const stored = readStored();
      const next: { theme: Theme; muted: boolean } = {
        theme: stored.theme ?? systemTheme(),
        muted: stored.muted ?? false,
      };
      setState((s) =>
        s.theme === next.theme && s.muted === next.muted ? s : next,
      );
    };

    window.addEventListener(SYNC_EVENT, handler);
    window.addEventListener('storage', storageHandler);
    return () => {
      window.removeEventListener(SYNC_EVENT, handler);
      window.removeEventListener('storage', storageHandler);
    };
  }, []);

  const setTheme = useCallback((t: Theme) => {
    setState((s) => ({ ...s, theme: t }));
  }, []);

  const setMuted = useCallback((m: boolean) => {
    setState((s) => ({ ...s, muted: m }));
  }, []);

  const toggleTheme = useCallback(() => {
    setState((s) => ({ ...s, theme: s.theme === 'light' ? 'dark' : 'light' }));
  }, []);

  const toggleMute = useCallback(() => {
    setState((s) => ({ ...s, muted: !s.muted }));
  }, []);

  const value: PreferencesValue = useMemo(
    () => ({ theme, setTheme, muted, setMuted, toggleTheme, toggleMute }),
    [theme, setTheme, muted, setMuted, toggleTheme, toggleMute],
  );

  return (
    <PreferencesContext.Provider value={value}>
      {children}
    </PreferencesContext.Provider>
  );
}

export function usePreferencesContext(): PreferencesValue {
  const ctx = useContext(PreferencesContext);
  if (!ctx) {
    throw new Error('usePreferences() called outside <PreferencesProvider>');
  }
  return ctx;
}
